import { TextArea } from '@HoudiniUI/TextArea';
import { css } from 'aphrodite';
import * as React from 'react';
import { InputFieldError } from '../../../components/InputFieldError';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { bs } from '../../../styles/styles';
import { aiEmailStepFormStyles } from './styles/AIEmailStepFormStyles';
import { Input } from '../../../components-v2/Input';

/** Mirrors AIEmailAutomationStep.MaxPromptLength on the API, which rejects anything longer. */
export const AI_EMAIL_PROMPT_MAX_LENGTH = 4000;

interface AIEmailStepFormProps {
	name: string;
	prompt: string;
	canEdit: boolean;
	isBusy: boolean;
	baseKey: string;
	errorMessage?: string | null;
	onNameChange: (name: string) => void;
	onNameBlur: () => void;
	onPromptChange: (prompt: string) => void;
	onPromptBlur: () => void;
	onClearError: () => void;
}

/**
 * Form for an AI email automation step: a title for the canvas, and the description the
 * model writes from. There is no subject or body field — both are written per recipient
 * when the step runs.
 */
export function AIEmailStepForm({
	name,
	prompt,
	canEdit,
	isBusy,
	baseKey,
	errorMessage,
	onNameChange,
	onNameBlur,
	onPromptChange,
	onPromptBlur,
	onClearError,
}: AIEmailStepFormProps) {
	const isDisabled = !canEdit || isBusy;
	const isOverLimit = prompt.length > AI_EMAIL_PROMPT_MAX_LENGTH;

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onNameChange(e.target.value);
		onClearError();
	};

	const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onPromptChange(e.target.value);
		onClearError();
	};

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				Email title: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<Input
				styles={[styleSheet.emailNameInput]}
				disabled={isDisabled}
				id={`automation-ai-email-step-name-input-${baseKey}`}
				key={`${baseKey}-name-input`}
				onBlur={onNameBlur}
				onChange={handleNameChange}
				onFocus={onClearError}
				placeholder='Enter a brief description of this email'
				type='text'
				value={name || ''}
			/>
			<div className={css(styleSheet.fieldLabel)}>
				What each email should say: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<TextArea
					disabled={isDisabled}
					id={`automation-ai-email-step-prompt-input-${baseKey}`}
					onBlur={onPromptBlur}
					onChange={handlePromptChange}
					onFocus={onClearError}
					placeholder={
						'Example: Remind them that their policy renews soon. If they had a claim in the last year, ' +
						'acknowledge it warmly and mention their adjuster by name.'
					}
					styles={[aiEmailStepFormStyles.promptTextArea]}
					value={prompt}
				/>
			</InputFieldError>
			<span
				className={css(
					aiEmailStepFormStyles.characterCount,
					isOverLimit && aiEmailStepFormStyles.characterCountOverLimit
				)}
			>
				{prompt.length} / {AI_EMAIL_PROMPT_MAX_LENGTH}
			</span>
			<p className={css(aiEmailStepFormStyles.description)}>
				Every contact gets their own email, written from what Levitate already knows about them and from the records
				this automation matched them on. The subject is written too. A contact the description plainly does not apply to
				is left out rather than sent something generic.
			</p>
		</>
	);
}
